import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCw, Home, HelpCircle } from 'lucide-react';
import React from 'react';

interface ProductionErrorCardProps {
  onRetry?: () => void;
  onGoHome?: () => void;
  errorCode?: string;
}

/**
 * Professional error card for production - hides technical details from users
 * Shows friendly message with actionable options
 */
const ProductionErrorCard: React.FC<ProductionErrorCardProps> = ({
  onRetry,
  onGoHome,
  errorCode
}) => {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--vscode-editor-background)' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-md w-full"
      >
        {/* Main Error Card */}
        <div 
          className="rounded-2xl p-8 border-2 shadow-2xl"
          style={{
            backgroundColor: 'var(--vscode-editor-background)',
            borderColor: 'var(--vscode-editorWarning-foreground)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}
        >
          {/* Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
            className="flex justify-center mb-6"
          >
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'var(--vscode-editorWarning-foreground)/20'
              }}
            >
              <AlertTriangle 
                className="w-10 h-10" 
                style={{ color: 'var(--vscode-editorWarning-foreground)' }}
              />
            </div>
          </motion.div>

          {/* Title */}
          <h1 
            className="text-2xl font-bold text-center mb-3"
            style={{ color: 'var(--vscode-editor-foreground)' }}
          >
            Oops! Something Went Wrong
          </h1>

          {/* Description */}
          <p 
            className="text-center mb-6 leading-relaxed"
            style={{ color: 'var(--vscode-descriptionForeground)' }}
          >
            We encountered an unexpected issue while processing your request. 
            Our team has been notified and we're working on it.
          </p>

          {/* Error Code (Optional) */}
          {errorCode && (
            <div 
              className="text-center mb-6 text-xs font-mono px-3 py-2 rounded"
              style={{
                backgroundColor: 'var(--vscode-input-background)',
                color: 'var(--vscode-descriptionForeground)'
              }}
            >
              Reference: {errorCode}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* Retry Button */}
            <button
              onClick={handleRetry}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-semibold transition-all"
              style={{
                backgroundColor: '#5B5BFF',
                color: '#FFFFFF',
                border: 'none',
                boxShadow: '0 4px 12px rgba(91, 91, 255, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4A4AEE';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(91, 91, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#5B5BFF';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(91, 91, 255, 0.3)';
              }}
            >
              <RefreshCw className="w-5 h-5" />
              <span>Try Again</span>
            </button>

            {/* Go Home Button */}
            <button
              onClick={handleGoHome}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-semibold transition-all border-2"
              style={{
                backgroundColor: 'var(--vscode-button-secondaryBackground)',
                color: 'var(--vscode-button-secondaryForeground)',
                borderColor: 'var(--vscode-button-border, transparent)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--vscode-button-secondaryHoverBackground)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--vscode-button-secondaryBackground)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Home className="w-5 h-5" />
              <span>Go to Dashboard</span>
            </button>
          </div>

          {/* Help Text */}
          <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--vscode-panel-border)' }}>
            <div className="flex items-start space-x-2">
              <HelpCircle 
                className="w-5 h-5 flex-shrink-0 mt-0.5" 
                style={{ color: 'var(--vscode-descriptionForeground)' }}
              />
              <div>
                <p className="text-sm" style={{ color: 'var(--vscode-descriptionForeground)' }}>
                  <strong>Need help?</strong>
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--vscode-descriptionForeground)' }}>
                  If this issue persists, please contact support with the reference code above.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Animated Background Elements */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
          <motion.div
            className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl opacity-10"
            style={{ backgroundColor: 'var(--vscode-editorWarning-foreground)' }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.15, 0.1],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-10"
            style={{ backgroundColor: '#5B5BFF' }}
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1,
            }}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default ProductionErrorCard;
